import React, { createContext, useState, useEffect, useContext } from 'react';
import Cookies from 'universal-cookie';

const authAPI = require('../Networking/AuthenticationAPI');
const globalAPI = require('../Networking/API');

const cookies = new Cookies();

interface Subscription {
    id: string;
    creationDate: string; // Consider using Date if you parse it
    expirationDate: string; // Consider using Date if you parse it
    name: string;
    businessName: string;
    plan: string;
    profileImage: string | null;
    email: string;
    status: "active" | "expired" | "canceled" | "trial_expired"; // Adjust based on possible statuses
}

interface AuthContextType {
    token: string | null;
    subscription: Subscription | null;
    isAuthenticated: boolean;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    mcpAuth: (email: string, state: string) => Promise<string>;
    signup: (name: string, businessName: string | undefined, email: string, password: string, plan: string, subscription: string | null, token: string | null) => Promise<void>;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [loading, setLoading] = useState(true);
    const [token, setToken] = useState(cookies.get("token") || null);

    const isAuthenticated = !!subscription;

    useEffect(() => {
        if (token) {
            globalAPI.authenticateSubscription(token)
                .then((sub: any) => {
                    setSubscription(sub);
                }).catch((error: Error) => {
                    logout();
                }).finally(() => {
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    }, [token]);

    const login = async (email: string, password: string) => {
        try {
            const res = await authAPI.login(email, password);
            const subscription = await globalAPI.authenticateSubscription(res.token);

            const expires = new Date();
            expires.setDate(expires.getDate() + 90);
            cookies.set(
                "token",
                res.token,
                {
                    path: "/",
                    expires,
                });

            setToken(res.token);
            setSubscription(subscription);
            setLoading(false);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const signup = async (name: string, businessName: string | undefined, email: string, password: string, plan: string, subscription_id: string | null, token: string | null) => {
        try {
            const res = await authAPI.signUp(name, businessName, email, password, plan, subscription_id, token);
            const subscription = await globalAPI.authenticateSubscription(res.token);

            const expires = new Date();
            expires.setDate(expires.getDate() + 90);
            cookies.set("token", res.token, {
                path: "/",
                expires
            });

            setToken(res.token);
            setSubscription(subscription);
            setLoading(false);

        } catch (error) {
            return Promise.reject(error);
        }
    };

    const mcpAuth = async (email: string, state: string) => {
        try {
            const response = await fetch(globalAPI.host + 'mcp/auth', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, state })
            });

            const data = await response.json();
            return data.code;
        } catch (error) {
            return Promise.reject(error);
        }
    };

    const logout = () => {
        cookies.remove("token");
        setToken(null);
        setSubscription(null);
    };

    return (
        <AuthContext.Provider
            value={{ subscription, isAuthenticated, login, logout, signup, mcpAuth, loading, token }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error("useAuth must be used within an AuthProvider");
    return context;
};